import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToastContext } from "@finitless/shared/contexts";
import { useApiStore } from "@finitless/shared/stores";
import { useForgotPassword, useResetPassword } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../../hooks/useUiLocale";
import { Button, Icon, IconButton, Input, LocaleSelector } from "../ui";

const ORDERING_DARK_LOCKUP =
  "/brand/ordering/1.2.1/logos/ordering-lockup-color-reversed.svg";

const ForgotPasswordPage = () => {
  const { t } = useTranslation(["auth", "app", "toasts"]);
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const random = searchParams.get("random");
  const projectFromParams = searchParams.get("project");

  const hasResetParams = !!(code || random);
  const isResetMode = !!(code && random);

  const [email, setEmail] = useState("");
  const [project, setProject] = useState(projectFromParams || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const forgotPasswordMutation = useForgotPassword();
  const resetPasswordMutation = useResetPassword();
  const setProjectConfig = useApiStore((state) => state.setProject);
  const toast = useToastContext();

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  const handleBackToLogin = () => {
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (hasResetParams && !isResetMode) {
      toast.error(t("validation.invalidResetLink", { ns: "auth" }));
      setTimeout(() => {
        handleBackToLogin();
      }, 2000);
    }
  }, [hasResetParams, isResetMode, toast, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isResetMode) {
      if (!password || !confirmPassword || !project) {
        toast.error(t("validation.fillAllFields", { ns: "auth" }));
        return;
      }

      if (password !== confirmPassword) {
        toast.error(t("validation.passwordsDoNotMatch", { ns: "auth" }));
        return;
      }

      if (password.length < 8) {
        toast.error(t("validation.passwordMinLength", { ns: "auth" }));
        return;
      }

      try {
        setProjectConfig(project);

        await resetPasswordMutation.mutateAsync({
          code,
          random,
          password,
          "confirm-password": confirmPassword,
        });

        toast.success(t("auth.resetPasswordSuccess", { ns: "toasts" }));
        setTimeout(() => {
          handleBackToLogin();
        }, 2000);
      } catch (error) {
        toast.error(
          error.message || t("auth.resetPasswordFailed", { ns: "toasts" }),
        );
      }
      return;
    }

    if (!email || !project) {
      toast.error(t("validation.fillAllFields", { ns: "auth" }));
      return;
    }

    try {
      setProjectConfig(project);
      const response = await forgotPasswordMutation.mutateAsync({ email });

      if (response.attemptsRemaining <= 2 && response.attemptsRemaining > 0) {
        toast.success(
          t("auth.forgotPasswordSentRemaining", {
            ns: "toasts",
            count: response.attemptsRemaining,
          }),
        );
      } else if (response.attemptsRemaining === 0) {
        toast.success(t("auth.forgotPasswordFinalAttempt", { ns: "toasts" }));
      } else {
        toast.success(t("auth.forgotPasswordSent", { ns: "toasts" }));
      }

      setTimeout(() => {
        handleBackToLogin();
      }, 2000);
    } catch (error) {
      toast.error(
        error.message || t("auth.forgotPasswordFailed", { ns: "toasts" }),
      );
    }
  };

  const isPending = isResetMode
    ? resetPasswordMutation.isPending
    : forgotPasswordMutation.isPending;

  const pageTitle = isResetMode
    ? t("resetPasswordTitle", { ns: "auth" })
    : t("forgotPasswordTitle", { ns: "auth" });
  const pageSubtitle = isResetMode
    ? t("resetPasswordSubtitle", { ns: "auth" })
    : t("forgotPasswordSubtitle", { ns: "auth" });

  return (
    <main className="forgot-password-page flex min-h-dvh min-h-screen items-center justify-center bg-[var(--od-ink-950)] px-4 py-5 text-[var(--od-white)] sm:px-6 sm:py-8 lg:px-8">
      <div className="w-full max-w-md space-y-3 sm:space-y-4">
        <LocaleSelector
          id="forgot-locale-selector"
          label={t("language.label", { ns: "app" })}
          showLeadingIcon={false}
          value={locale}
          onChange={handleLocaleChange}
          options={supportedLocales}
          containerClassName="w-full justify-end"
          labelClassName="text-[var(--od-ink-300)]"
          selectClassName="h-11 min-w-[124px] rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-900)] text-[13px] text-[var(--od-white)] shadow-none backdrop-blur-none focus:border-[var(--od-blue-400)] focus:ring-[var(--od-blue-600)]/30"
        />

        <section className="rounded-[var(--od-radius-panel)] border border-[var(--od-ink-600)] bg-[var(--od-ink-900)] p-5 shadow-[var(--od-shadow-1)] sm:p-7">
          <div className="text-center">
            <div className="mx-auto mb-5 flex min-h-8 items-center justify-center">
              <img
                className="h-7 w-auto max-w-full min-w-[120px] sm:h-8"
                src={ORDERING_DARK_LOCKUP}
                alt="Ordering.co"
              />
            </div>
            <h1 className="text-2xl font-semibold tracking-[-0.025em] text-[var(--od-white)] sm:text-[1.75rem]">
              {pageTitle}
            </h1>
            <p className="mt-2 text-sm leading-6 text-[var(--od-ink-300)]">
              {pageSubtitle}
            </p>
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-3">
              <Input
                id="project"
                name="project"
                type="text"
                autoComplete="off"
                required
                value={project}
                onChange={(event) => setProject(event.target.value)}
                placeholder={t("placeholders.project", { ns: "auth" })}
                disabled={isPending}
                label={t("fields.projectName", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-[var(--od-ink-300)]"
                className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
              />

              {!isResetMode && (
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("placeholders.email", { ns: "auth" })}
                  disabled={isPending}
                  label={t("fields.emailAddress", { ns: "auth" })}
                  variant="dark"
                  size="lg"
                  labelClassName="text-[var(--od-ink-300)]"
                  className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
                />
              )}

              {isResetMode && (
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t("placeholders.newPassword", { ns: "auth" })}
                  disabled={isPending}
                  label={t("fields.newPassword", { ns: "auth" })}
                  variant="dark"
                  size="lg"
                  labelClassName="text-[var(--od-ink-300)]"
                  className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] pr-14 text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
                  rightIcon={
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="iconTouch"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isPending}
                      aria-pressed={showPassword}
                      aria-label={
                        showPassword
                          ? t("accessibility.hidePassword", { ns: "auth" })
                          : t("accessibility.showPassword", { ns: "auth" })
                      }
                      className="rounded-[var(--od-radius-control)] text-[var(--od-ink-300)] hover:bg-[var(--od-ink-700)] hover:text-[var(--od-white)] focus-visible:ring-[var(--od-blue-600)] motion-reduce:transition-none"
                    >
                      <Icon name={showPassword ? "EyeOff" : "Eye"} />
                    </IconButton>
                  }
                />
              )}

              {isResetMode && (
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder={t("placeholders.confirmPassword", {
                    ns: "auth",
                  })}
                  disabled={isPending}
                  label={t("fields.confirmPassword", { ns: "auth" })}
                  variant="dark"
                  size="lg"
                  labelClassName="text-[var(--od-ink-300)]"
                  className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] pr-14 text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
                  rightIcon={
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="iconTouch"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      disabled={isPending}
                      aria-pressed={showConfirmPassword}
                      aria-label={
                        showConfirmPassword
                          ? t("accessibility.hidePassword", { ns: "auth" })
                          : t("accessibility.showPassword", { ns: "auth" })
                      }
                      className="rounded-[var(--od-radius-control)] text-[var(--od-ink-300)] hover:bg-[var(--od-ink-700)] hover:text-[var(--od-white)] focus-visible:ring-[var(--od-blue-600)] motion-reduce:transition-none"
                    >
                      <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} />
                    </IconButton>
                  }
                />
              )}
            </div>

            <Button
              type="submit"
              size="touch"
              disabled={isPending}
              aria-busy={isPending || undefined}
              className="w-full rounded-[var(--od-radius-control)] bg-[var(--od-blue-600)] text-[var(--od-white)] shadow-[var(--od-shadow-blue)] hover:bg-[var(--od-blue-700)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--od-ink-900)] motion-reduce:transition-none"
            >
              {isPending ? (
                <span className="flex items-center">
                  <Icon
                    name="Loader2"
                    className="mr-3 -ml-1 animate-spin motion-reduce:animate-none"
                  />
                  {isResetMode
                    ? t("resettingPassword", { ns: "auth" })
                    : t("sendingResetLink", { ns: "auth" })}
                </span>
              ) : isResetMode ? (
                t("resetPasswordButton", { ns: "auth" })
              ) : (
                t("sendResetLink", { ns: "auth" })
              )}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                size="touch"
                onClick={handleBackToLogin}
                disabled={isPending}
                className="h-auto min-h-11 px-2 text-[var(--od-blue-300)] hover:text-[var(--od-blue-200)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--od-ink-900)] motion-reduce:transition-none"
              >
                {t("backToSignIn", { ns: "auth" })}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default ForgotPasswordPage;
